import type { ServerConnection } from "@/context/server"
import { controlApiFetch } from "@/transport/control-api"
import { localMode } from "@/transport/local-mode"
import { serverFetch } from "@/transport/server"

/**
 * Provider connections and saved secrets live on the account's machines, not
 * the control plane: every machine holds the same ones. The fleet registers
 * how to reach one that is online.
 */
type AccountMachineResolver = (signal?: AbortSignal) => Promise<ServerConnection.HttpBase | undefined>

let resolver: AccountMachineResolver | undefined

/** Registers resolve as the way to reach a machine; returns its removal. */
export function setAccountMachineResolver(resolve: AccountMachineResolver) {
  resolver = resolve
  return () => {
    if (resolver === resolve) resolver = undefined
  }
}

export class NoAccountMachineError extends Error {
  constructor() {
    super("None of your machines is online. Your providers and secrets are kept on your machines; turn one on.")
    this.name = "NoAccountMachineError"
  }
}

/**
 * Fetches an account API path (such as /api/providers) that a machine serves:
 * this machine's own account in local mode, otherwise an online machine of
 * the account.
 */
export async function accountMachineFetch(path: string, init?: RequestInit) {
  if (localMode()) return controlApiFetch(path, init)
  const server = await resolver?.(init?.signal ?? undefined)
  if (!server) throw new NoAccountMachineError()
  return serverFetch(server, `/api/account${path.startsWith("/") ? path : `/${path}`}`, init)
}
